import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonIcon,
  Modal,
  Input,
} from '@rabbit/elements/shared-components';
import {
  nestApiCreateCategory,
  nestApiUpdateCategory,
  nestApiDeleteCategory,
  nestApiGetCategory,
  nestApiGetAllCategories,
} from '@rabbit/bizproc/core';
import { DTCategory_Public } from '@rabbit/data/types';
import { useEffect, useMemo, useState, useRef } from 'react';
import { CreateTable } from '@rabbit/sage/utils/CreateTable';
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { AppContext } from '@rabbit/app-context';
import { useContext } from 'react';

interface CategoryListShape {
  docid: string;
  id: string;
  key: string;
  name: string;
  level: number;
  lineage: string[];
  tenantLink?: string;
  hasSubcategories?: boolean;
  searchableName?: string; // Clean name without indentation prefixes for search
}

export default function CategoryTab() {
  const { t } = useTranslation();
  const { config } = useContext(AppContext);
  const formRef = useRef(null) as any;
  const [isLoading, setIsLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<DTCategory_Public | null>(null);
  const [categoryList, setCategoryList] = useState<DTCategory_Public[]>([]);
  const [parentCategory, setParentCategory] =
    useState<DTCategory_Public | null>(null);
  const [isSubcategoryMode, setIsSubcategoryMode] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await nestApiGetAllCategories({
          tenantLink: t('tenantLink'),
        });
        if (response && response.data) {
          setCategoryList(response.data);

          // Set all top-level categories and their immediate subcategories to be expanded by default
          const topLevelCategories = response.data.filter(
            (category) => category.level === 0
          );

          const level1Categories = response.data.filter(
            (category) => category.level === 1
          );

          const initialExpandedState: Record<string, boolean> = {};

          // Expand all top-level categories
          topLevelCategories.forEach((category) => {
            initialExpandedState[category.docid] = true;
          });

          // Expand all level 1 categories (subcategories)
          level1Categories.forEach((category) => {
            initialExpandedState[category.docid] = true;
          });

          setExpandedCategories(initialExpandedState);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [t]);

  const modalSettings = {
    kind: 'generic' as const,
    settings: {
      title: isSubcategoryMode
        ? t('general.addSubcategory')
        : selectedCategory
        ? t('general.editCategory')
        : t('general.addCategory'),
      text: isSubcategoryMode
        ? t('general.addSubcategory')
        : selectedCategory
        ? t('general.editCategory')
        : t('general.addCategory'),
      handleClose: () => {
        setEditModalOpen(false);
        setSelectedCategory(null);
        setIsSubcategoryMode(false);
        setParentCategory(null);
      },
    },
  };

  const deleteModalSettings = {
    kind: 'pop-up' as const,
    settings: {
      title: t('general.deleteCategory'),
      text: t('Are you sure you want to delete this category?'),
      primaryButtonText: t('general.delete'),
      outlineButtonText: t('general.cancel'),
      handleClose: () => {
        setDeleteModalOpen(false);
        setSelectedCategory(null);
      },
      handleOutlineClick: () => {
        setDeleteModalOpen(false);
        setSelectedCategory(null);
      },
      handlePrimaryClick: async () => {
        if (selectedCategory) {
          // Pass docid as docId to match the DocIdParamDto
          await handleDelete(selectedCategory.docid);
          setDeleteModalOpen(false);
          setSelectedCategory(null);
        }
      },
    },
  };

  const schema = Yup.object({
    titles: Yup.object({
      en: Yup.string().required(t('message.pleaseEnterAName')),
    }),
    key: Yup.string(),
    level: Yup.number().required(),
    lineage: Yup.array().of(Yup.string()),
    tenantLink: Yup.string(),
  });

  const getInitialValues = () => {
    if (isSubcategoryMode && parentCategory) {
      // For subcategory, we pre-fill with parent's lineage and increment level
      return {
        titles: { en: '' },
        key: '',
        level: parentCategory.level + 1,
        lineage: [...parentCategory.lineage, parentCategory.key],
        tenantLink: parentCategory.tenantLink || t('tenantLink'),
      };
    }

    if (selectedCategory) {
      // For editing existing category
      return {
        titles: selectedCategory.titles || { en: '' },
        key: selectedCategory.key || '',
        level: selectedCategory.level || 0,
        lineage: selectedCategory.lineage || [],
        tenantLink: selectedCategory.tenantLink || t('tenantLink'),
      };
    }

    // For new top-level category
    return {
      titles: { en: '' },
      key: '',
      level: 0,
      lineage: [],
      tenantLink: t('tenantLink'),
    };
  };

  const handleSubmit = async (values: {
    titles: { en: string; [lang: string]: string };
    key: string;
    level: number;
    lineage: string[];
    tenantLink?: string;
  }) => {
    try {
      // Generate key if not provided
      if (!values.key) {
        values.key = values.titles.en
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');
      }

      if (selectedCategory && !isSubcategoryMode) {
        // Update existing category
        await handleUpdate(selectedCategory.docid, {
          input: values,
        });
      } else {
        // Create new category or subcategory
        await handleCreate(values);
      }

      setEditModalOpen(false);
      setIsSubcategoryMode(false);
      setParentCategory(null);
      setSelectedCategory(null);
    } catch (error) {
      console.error('Failed to submit form:', error);
    }
  };

  const handleEditModal = (itemDetails?: CategoryListShape) => {
    if (itemDetails) {
      setSelectedCategory(
        categoryList.find((c) => c.docid === itemDetails.docid) || null
      );
    } else {
      setSelectedCategory(null);
    }
    setIsSubcategoryMode(false);
    setEditModalOpen(true);
  };

  const handleAddSubcategory = (parent: CategoryListShape) => {
    const parentCategory =
      categoryList.find((c) => c.docid === parent.docid) || null;
    if (parentCategory) {
      setParentCategory(parentCategory);
      setIsSubcategoryMode(true);
      setSelectedCategory(null);
      setEditModalOpen(true);
    }
  };

  const handleCreate = async (data: any) => {
    try {
      const response = await nestApiCreateCategory(data);
      if (response && response.data) {
        setCategoryList((prevList) => [...prevList, response.data]);
      }
    } catch (error) {
      console.error('Failed to create category:', error);
    }
  };

  const handleUpdate = async (docId: string, data: any) => {
    try {
      const response = await nestApiUpdateCategory({
        docid: docId,
        ...data.input,
      });
      if (response && response.data) {
        setCategoryList((prevList) =>
          prevList.map((item) => (item.docid === docId ? response.data : item))
        );
      }
    } catch (error) {
      console.error('Failed to update category:', error);
    }
  };

  const handleDelete = async (docid: string) => {
    console.log('Deleting category:', docid);
    try {
      // Ensure we're using the correct parameter name for the API call
      // The DocIdParamDto expects a property named 'docId' (with capital 'I')
      const response = await nestApiDeleteCategory({ docid });
      if (response && response.data) {
        setCategoryList((prevList) =>
          prevList.filter((item) => item.docid !== docid)
        );
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
    }
  };

  const toggleCategoryExpansion = (docid: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [docid]: !prev[docid],
    }));
  };

  const expandAllCategories = () => {
    const allCategories = categoryList.reduce((acc, category) => {
      acc[category.docid] = true;
      return acc;
    }, {} as Record<string, boolean>);

    setExpandedCategories(allCategories);
  };

  const collapseAllCategories = () => {
    setExpandedCategories({});
  };

  const CategoryTable = useMemo(() => {
    const categoryListOptions: CategoryListShape[] =
      categoryList?.map((category) => {
        // Check if this category is a parent of any other category
        const hasSubcategories = categoryList.some(
          (c) => c.lineage?.includes(category.key) && c.level > category.level
        );

        return {
          docid: category.docid,
          id: category.docid,
          key: category.key,
          name: category.titles?.en || '',
          level: category.level,
          lineage: category.lineage || [],
          tenantLink: category.tenantLink,
          hasSubcategories,
        };
      }) || [];

    // Create a hierarchical structure for display
    let displayCategories: CategoryListShape[] = [];

    // Add top-level categories (level 0)
    const topLevelCategories = categoryListOptions.filter(
      (category) => category.level === 0
    );

    // Add top-level categories and their subcategories if expanded
    topLevelCategories.forEach((category) => {
      displayCategories.push(category);

      // If this category is expanded, add its direct subcategories
      if (expandedCategories[category.docid]) {
        const directSubcategories = categoryListOptions.filter(
          (c) => c.lineage?.includes(category.key) && c.level === 1
        );

        directSubcategories.forEach((subcat) => {
          // Add visual indication that this is a subcategory
          const subcatWithIndent = {
            ...subcat,
            name: `— ${subcat.name}`, // Add indentation to show hierarchy
          };
          displayCategories.push(subcatWithIndent);

          // If this subcategory is expanded, add its subcategories
          if (expandedCategories[subcat.docid]) {
            const nestedSubcategories = categoryListOptions.filter(
              (c) => c.lineage?.includes(subcat.key) && c.level === 2
            );

            nestedSubcategories.forEach((nestedSubcat) => {
              displayCategories.push({
                ...nestedSubcat,
                name: `—— ${nestedSubcat.name}`, // Double indentation
              });
            });
          }
        });
      }
    });

    // Process categories to add searchableName property (clean name without indentation prefixes)
    displayCategories.forEach((category) => {
      // Get the clean name for search functionality by removing indentation prefixes
      let cleanName = category.name;
      if (category.level === 1) {
        cleanName = category.name.replace(/^— /, '');
      } else if (category.level === 2) {
        cleanName = category.name.replace(/^—— /, '');
      }

      // Store the clean name for search functionality
      category.searchableName = cleanName;
    });

    return CreateTable<CategoryListShape>(displayCategories, [
      // Hidden column for global search - not displayed but used for filtering

      {
        header: t('general.categoryName'),
        accessorKey: 'name',
        size: 300,
        enableGlobalFilter: false,
        value: (category) => {
          // Check if this category has subcategories
          const hasSubcats = category.hasSubcategories;

          // Get the clean name without indentation prefixes that was set during preprocessing
          const cleanName = category.searchableName || category.name;

          // For level 0 (top-level) categories with subcategories
          if (hasSubcats && category.level === 0) {
            return (
              <div className="flex items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategoryExpansion(category.docid);
                  }}
                  className="mr-2 rounded p-1 hover:bg-gray-200"
                >
                  {expandedCategories[category.docid] ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                </button>
                {category.name}
              </div>
            );
          }

          // For level 1 subcategories that have their own subcategories
          if (hasSubcats && category.level === 1) {
            // Extract the actual name without the indentation prefix
            const actualName = cleanName;

            return (
              <div className="flex items-center">
                <span className="ml-4"></span> {/* Indentation */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategoryExpansion(category.docid);
                  }}
                  className="mr-2 rounded p-1 hover:bg-gray-200"
                >
                  {expandedCategories[category.docid] ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                </button>
                {actualName}
              </div>
            );
          }

          // For level 1 subcategories without their own subcategories
          if (category.level === 1 && !hasSubcats) {
            const actualName = cleanName;
            return (
              <div className="flex items-center">
                <span className="ml-4"></span> {/* Indentation */}
                <span className="ml-7"></span>{' '}
                {/* Space where the button would be */}
                {actualName}
              </div>
            );
          }

          // For level 2 subcategories
          if (category.level === 2) {
            const actualName = cleanName;
            return (
              <div className="flex items-center">
                <span className="ml-8"></span> {/* Double indentation */}
                <span className="ml-7"></span>{' '}
                {/* Space where the button would be */}
                {actualName}
              </div>
            );
          }

          return category.name;
        },
      },
      {
        header: t('general.searchCategory'),
        accessorKey: 'searchableName',
        enableGlobalFilter: true,
        size: 0,
        maxSize: 0,
        enableColumnFilter: false,
        enableColumnActions: false,
        enableSorting: false,
        enableHiding: false,
        value: (category) => category.searchableName || category.name,
      },
      {
        header: t('general.subcategory') + ' ' + t('general.count'),
        accessorKey: 'subcategoryCount',
        size: 100,
        value: (category) => {
          // Count direct subcategories for this category
          const subcategoryCount = categoryList.filter(
            (c) =>
              c.lineage?.includes(category.key) &&
              c.level === category.level + 1
          ).length;
          return subcategoryCount.toString();
        },
      },
      {
        header: t('general.categoryLevel'),
        accessorKey: 'level',
        size: 20,
        value: (category) => category.level.toString(),
      },
      {
        header: '',
        accessorKey: 'actions',
        value: (category) => (
          <div className="mr-4 flex justify-center gap-2">
            {config.CATEGORY.MANAGEMENT.CAN_ADD_NEW && (
              <ButtonIcon
                label={t('general.addSubcategory')}
                kind={'bgGreen'}
                iconLeft={true}
                Icon={PlusIcon}
                onClick={() => handleAddSubcategory(category)}
              />
            )}
            <ButtonIcon
              label={''}
              kind={'bgLightGreen'}
              iconLeft={true}
              Icon={PencilSquareIcon}
              onClick={() => handleEditModal(category)}
            />

            {config.CATEGORY.MANAGEMENT.CAN_DELETE && (
              <ButtonIcon
                label={''}
                kind={'hoverBgRed'}
                iconLeft={true}
                Icon={TrashIcon}
                onClick={() => {
                  setSelectedCategory(
                    categoryList.find((c) => c.docid === category.docid) || null
                  );
                  setDeleteModalOpen(true);
                }}
              />
            )}
          </div>
        ),
        size: 30,
        maxSize: 40,
      },
    ]);
  }, [
    categoryList,
    isLoading,
    t,
    config.CATEGORY.MANAGEMENT.CAN_ADD_NEW,
    config.CATEGORY.MANAGEMENT.CAN_DELETE,
    expandedCategories,
  ]);

  // If category management is not enabled, don't render anything
  if (!config.CATEGORY.MANAGEMENT.ENABLED) {
    return null;
  }

  return (
    <>
      <div className="relative z-10 flex w-full items-center justify-between py-4">
        <div className="absolute top-[60px] right-5">
          <div className="flex gap-2">
            {/* Show Expand All button if there are no expanded categories, otherwise show Collapse All */}
            {Object.keys(expandedCategories).length === 0 ? (
              <ButtonIcon
                label={t('general.expandAll')}
                iconLeft={true}
                kind={'activeBgLightGreen'}
                onClick={expandAllCategories}
              />
            ) : (
              <ButtonIcon
                label={t('general.collapseAll')}
                iconLeft={true}
                kind={'activeBgLightGreen'}
                onClick={collapseAllCategories}
              />
            )}
            {config.CATEGORY.MANAGEMENT.CAN_ADD_NEW && (
              <ButtonIcon
                label={t('general.addCategory')}
                iconLeft={true}
                onClick={() => handleEditModal()}
              />
            )}
          </div>
        </div>
      </div>
      <CategoryTable
        initialState={{
          columnPinning: { right: ['actions'] },
          showGlobalFilter: true,
          isLoading,
        }}
        muiSearchTextFieldProps={{
          placeholder: t('general.searchCategory'),
          sx: {
            minWidth: '600px',
            borderRadius: '20px',
            backgroundColor: '#fff',
            margin: '5px 0',
            '& .MuiOutlinedInput-root': {
              '& > fieldset': { borderRadius: '10px' },
            },
          },
          variant: 'outlined',
          margin: 'none',
        }}
      />
      {editModalOpen && (
        <Modal
          className="lg:w-[724px]"
          kind={'generic'}
          settings={modalSettings.settings}
        >
          <Formik
            onSubmit={handleSubmit}
            initialValues={getInitialValues()}
            validationSchema={schema}
            validateOnChange={true}
            validateOnBlur={false}
            innerRef={formRef}
          >
            {(props) => (
              <Form className="mt-2 w-full px-5">
                {isSubcategoryMode && parentCategory && (
                  <div className="w-full py-2">
                    <div className="font-nunito mb-1 text-sm text-gray-700">
                      {t('general.parentCategory')}
                    </div>
                    <div className="rounded-md bg-gray-100 p-2">
                      {parentCategory.titles?.en || parentCategory.key}
                    </div>
                  </div>
                )}
                <div className="w-full py-2">
                  <Input
                    type="text"
                    label={`${t('general.categoryName')}*`}
                    name="titles.en"
                    settings={{
                      id: 'titles.en',
                      hint: '*required',
                      placeholder: t('general.categoryName'),
                    }}
                  />
                </div>

                <div className="mt-8 flex gap-5 py-2">
                  <Button
                    kind={'primary'}
                    type="submit"
                    disabled={props.isSubmitting || !props.isValid}
                  >
                    {t('general.save')}
                  </Button>
                  <Button
                    kind={'red'}
                    onClick={() => {
                      setEditModalOpen(false);
                      setIsSubcategoryMode(false);
                      setParentCategory(null);
                      setSelectedCategory(null);
                    }}
                  >
                    {t('general.cancel')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          className="h-fit rounded-md bg-white lg:w-[324px]"
          kind={'pop-up'}
          settings={deleteModalSettings.settings}
        />
      )}
    </>
  );
}
